/**
 * TD-28 段階④: フィールド定義駆動のフォーム描画 (D-10)。
 * request_type_field_definitions の field_type ごとに入力 UI を出し分け、
 * request_type_field_conditions (show/hide/require/optional) と _formulas を
 * クライアント側でも評価して即時に反映する。
 *
 * ⚠️ 表示・入力補助のためのミラー実装であり、正はサーバー (apps/api/src/lib/request-instances/form.ts)。
 * 提出時にサーバーが同じ AST を評価し直し、is_computed の値は必ずサーバー側で再計算される。
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { ConfirmDialog } from '@/components/Modal';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { evaluateAstSafe, type AstValue } from '@/lib/request-form-ast';
import { validateFormFields } from '@/lib/request-form-validation';

export type FieldOption = { value: string; label: string };
export type FieldDefinition = {
  field_definition_id: string;
  field_key: string;
  field_type: string;
  // 使い勝手フィードバック② (D-U25): サーバー側で解決済みの表示名。常に非 null (field_key へ
  // フォールバック済み)。⚠️ field_key はここでも保持し続ける (計算式・条件式の field_ref が
  // 参照する安定識別子のため、表示にはこちらを使わない)。
  label: string;
  field_options: FieldOption[] | null;
  semantic_role: string | null;
  is_computed: boolean;
  is_required_default: boolean;
  sort_order: number;
  // TD-28 D-26: レイアウトは「表示順 + セクション分け」まで (自由なフォームビルダーにはしない)
  section_label?: string | null;
  section_order?: number;
  // 使い勝手フィードバック⑥ (D-U28): 層1 (上限・範囲) + 層2 (書式)。未設定 = 制約なし。
  validation_json?: Record<string, unknown> | null;
};
export type FieldCondition = {
  condition_id: string;
  target_field_definition_id: string;
  effect: string;
  expression_ast: unknown;
  sort_order: number;
  // 使い勝手フィードバック⑥ (D-U28): effect='error' のときのメッセージ。
  error_message?: string | null;
};
export type FieldFormula = {
  formula_id: string;
  target_field_definition_id: string;
  expression_ast: unknown;
};
export type FormValues = Record<string, unknown>;
export type ResolveWorkDaysFn = (
  start: string, end: string,
) => Promise<{ workDays: string[]; fromStandingPattern: number } | null>;

// 使い勝手フィードバック増分⑧ (D-U26/D-U31): ファイル添付。
export type AttachmentItem = {
  attachment_id: string; field_definition_id: string; file_name: string;
  mime_type: string; byte_size: number; uploaded_at: string;
};
/** GET /request-instances/:id/attachments のレスポンス形。level='exists_only' は items が null
 *  (件数のみ、詳細設計 §10-5)。 */
export type AttachmentSummary = { count: number; items: AttachmentItem[] | null };
/** プラットフォームの上限の上限 (サーバー側 PLATFORM_MAX_FILE_COUNT と同じ値)。
 *  ⚠️ クライアント側はあくまで UX 用のミラーであり、最終判定は必ずサーバー (§7-4 と同じ思想)。 */
const PLATFORM_MAX_FILE_COUNT = 5;
const resolveMaxFileCountClient = (f: FieldDefinition): number => {
  const raw = f.validation_json?.max_file_count;
  return typeof raw === 'number' ? Math.min(raw, PLATFORM_MAX_FILE_COUNT) : PLATFORM_MAX_FILE_COUNT;
};
/** design-expert MEDIUM-2指摘: サーバー側 MAX_REQUEST_ATTACHMENT_SIZE_BYTES と同じ値。
 *  低速回線で10MB超のファイルを選んでからサーバーエラーで気づく事故を避けるため、
 *  選択直後にクライアント側でも弾く (最終判定は必ずサーバー、§7-4と同じ思想)。 */
const MAX_ATTACHMENT_SIZE_BYTES = 10 * 1024 * 1024;
/** ⚠️ 新規作成時 (requestInstanceId が未確定) は、design-expert H-5 のとおり「先に保存してください」
 *  と言わせず、最初のアップロード操作で下書きを自動作成する。呼び出し元 (RequestsPage.tsx) が
 *  実装し、既存 draft があればその id を、無ければ POST /request-instances を実行して新しい id を返す。 */
export type AttachmentCallbacks = {
  requestInstanceId: string | null;
  ensureRequestInstanceId: () => Promise<string>;
  uploadAttachment: (requestInstanceId: string, fieldDefinitionId: string, file: File) => Promise<AttachmentItem>;
  deleteAttachment: (requestInstanceId: string, attachmentId: string) => Promise<void>;
  fetchAttachmentBlob: (requestInstanceId: string, attachmentId: string) => Promise<Blob>;
  /** アップロード・削除が成功した後、呼び出し元に一覧の再取得を促す。 */
  onAttachmentsChanged: () => void;
};

const toAstValue = (v: unknown): AstValue => {
  if (v === undefined || v === null || v === '') return null;
  if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') return v;
  return null;
};

/** 計算式を反復適用して is_computed フィールドの表示値を求める (サーバーの applyFormulas と同じ意味論)。 */
export const computeDerivedValues = (
  fields: FieldDefinition[], formulas: FieldFormula[], values: FormValues,
): FormValues => {
  const byTarget = new Map(formulas.map((f) => [f.target_field_definition_id, f]));
  // ⚠️ 5並列レビュー HIGH (design-and-code): 「is_computed だから」ではなく「計算式が実際にある
  // フィールドだから」入力値を破棄する。提出済み申請の詳細は loadFormSpecAsOf が formulas を
  // 返さない (履歴表示では表示制御を適用しない設計) ため、is_computed 全件を無条件に null で
  // 潰すと DB に保存済みの計算済みの値 (消化日数など) が空欄で表示されてしまう。
  const computed = fields.filter((f) => f.is_computed && byTarget.has(f.field_definition_id));
  if (computed.length === 0) return values;
  const next: FormValues = { ...values };
  for (const f of computed) next[f.field_definition_id] = null;
  for (let pass = 0; pass < computed.length; pass += 1) {
    let changed = false;
    const map = new Map<string, AstValue>(fields.map((f) => [f.field_definition_id, toAstValue(next[f.field_definition_id])]));
    for (const f of computed) {
      if (next[f.field_definition_id] !== null) continue;
      const formula = byTarget.get(f.field_definition_id);
      if (!formula) continue;
      const result = evaluateAstSafe(formula.expression_ast, map);
      if (result !== null) { next[f.field_definition_id] = result; changed = true; }
    }
    if (!changed) break;
  }
  return next;
};

export type FieldState = { visible: boolean; required: boolean };

/** 条件を sort_order 昇順に後勝ちで適用する (サーバーの resolveFieldStates と同じ意味論)。 */
export const resolveStates = (
  fields: FieldDefinition[], conditions: FieldCondition[], values: FormValues,
): Map<string, FieldState> => {
  const states = new Map<string, FieldState>(
    fields.map((f) => [f.field_definition_id, { visible: true, required: f.is_required_default }]),
  );
  const map = new Map<string, AstValue>(fields.map((f) => [f.field_definition_id, toAstValue(values[f.field_definition_id])]));
  for (const cond of [...conditions].sort((a, b) => a.sort_order - b.sort_order)) {
    const state = states.get(cond.target_field_definition_id);
    if (!state) continue;
    if (evaluateAstSafe(cond.expression_ast, map) !== true) continue;
    if (cond.effect === 'show') state.visible = true;
    else if (cond.effect === 'hide') state.visible = false;
    else if (cond.effect === 'require') state.required = true;
    else if (cond.effect === 'optional') state.required = false;
  }
  return states;
};

const inputCls = 'w-full min-h-[44px] sm:min-h-[38px] rounded-md border border-surface-border px-3 py-2 text-sm text-text-body bg-white disabled:bg-surface-sunken disabled:text-text-sub';

type Props = {
  fields: FieldDefinition[];
  conditions: FieldCondition[];
  formulas: FieldFormula[];
  values: FormValues;
  onChange: (values: FormValues) => void;
  readOnly?: boolean;
  /** TD-28 D-24: 値を伏せる項目 (表示段階 exists_only)。項目名は出すが値は見せない。 */
  maskedFieldIds?: string[];
  /** 使い勝手フィードバック③ (D-U21): 表示はされるが編集はできない項目。
   *  ⚠️ `disabled` にせず読み取り専用として描画すること — 「①値をマスク ②自動計算 ③編集不可」の
   *  3種を同じ見た目に潰すと利用者が区別できなくなる (詳細設計 §4-4)。 */
  nonEditableFieldIds?: string[];
  /** TD-28 §17-1 (D-30): 所定労働日の自動判定。multi_date の初期値を埋めるためだけに使う
   *  入力支援であり、消化日の正はあくまでこのフィールドの値 (サーバーもこの値を消化日として扱う)。
   *  未指定なら自動入力ボタンを出さない。
   *  `fromStandingPattern` は「確定シフトではなく基本の勤務パターンから推定した日数」。 */
  onResolveWorkDays?: ResolveWorkDaysFn;
  /** 使い勝手フィードバック⑥ (D-U28): 提出を試みた後 true にする。true の間は blur していない
   *  項目のエラーも含めて全件表示し、先頭のエラー項目へフォーカスを移す (§7-5)。 */
  showAllErrors?: boolean;
  /** 使い勝手フィードバック⑥ (D-U28): 呼び出し元 (提出ボタン) が現在の入力値の妥当性を
   *  知るためのコールバック。エラーが1件も無ければ空の Map が渡る。 */
  onValidationChange?: (errors: Map<string, string>) => void;
  /** 使い勝手フィードバック増分⑧ (D-U26/D-U31): field_definition_id → 添付の要約。
   *  未指定 (undefined) の場合は「0件」として扱う。 */
  attachmentsByField?: Map<string, AttachmentSummary>;
  /** 増分⑧: 添付のアップロード・削除・プレビュー用コールバック。未指定なら file 型項目は
   *  読み取り専用の一覧表示のみになる (呼び出し元が対応していない画面向けのフォールバック)。 */
  attachments?: AttachmentCallbacks;
};

/** セクション (D-26) 単位にまとめる。section_order → sort_order の順に並べる。
 *  セクション未設定の項目は先頭の無名セクションに入る (既存の並びを変えない)。 */
export const groupBySection = (
  fields: FieldDefinition[],
): Array<{ key: string; label: string | null; order: number; fields: FieldDefinition[] }> => {
  // ⚠️ 5並列レビュー Minor (test-runner/design-and-code): `label ?? ''` をキーにすると
  // 「セクション未設定 (null)」と「空文字のセクション名」が同一グループに潰れ、
  // React の key も文字列リテラルと衝突しうる。null と文字列を明確に分ける接頭辞を付ける。
  const map = new Map<string, { key: string; label: string | null; order: number; fields: FieldDefinition[] }>();
  for (const f of fields) {
    const label = f.section_label ?? null;
    const key = label === null ? 'none:' : `s:${label}`;
    if (!map.has(key)) map.set(key, { key, label, order: f.section_order ?? 0, fields: [] });
    const group = map.get(key)!;
    // 同一ラベル内で section_order が食い違う場合は最小値を採る (先勝ちにしない)
    group.order = Math.min(group.order, f.section_order ?? 0);
    group.fields.push(f);
  }
  return [...map.values()]
    .map((g) => ({ ...g, fields: [...g.fields].sort((a, b) => a.sort_order - b.sort_order) }))
    // セクション未設定のグループは常に先頭 (既存の並びを変えないため)
    .sort((a, b) => {
      if ((a.label === null) !== (b.label === null)) return a.label === null ? -1 : 1;
      return (a.order - b.order) || (a.label ?? '').localeCompare(b.label ?? '');
    });
};

/** 使い勝手フィードバック③ (D-U21): 読み取り専用ブロックに出す値の文字列表現。
 *  native readOnly の意味論を持たない型 (真偽値・選択肢・期間・複数選択・複数日付) 向け。 */
const formatStaticValue = (f: FieldDefinition, value: unknown): string => {
  if (value === null || value === undefined || value === '') return '（未入力）';
  if (f.field_type === 'boolean') return value === true ? 'はい' : 'いいえ';
  if (f.field_type === 'select') {
    return (f.field_options ?? []).find((o) => o.value === value)?.label ?? String(value);
  }
  if (f.field_type === 'multi_select') {
    const arr = Array.isArray(value) ? (value as string[]) : [];
    if (arr.length === 0) return '（未入力）';
    const byValue = new Map((f.field_options ?? []).map((o) => [o.value, o.label]));
    return arr.map((v) => byValue.get(v) ?? v).join('、');
  }
  if (f.field_type === 'multi_date') {
    const arr = Array.isArray(value) ? (value as string[]) : [];
    return arr.length > 0 ? arr.join('、') : '（未入力）';
  }
  if (f.field_type === 'date_range' && typeof value === 'object' && !Array.isArray(value)) {
    const { start, end } = value as { start?: string; end?: string };
    return start || end ? `${start ?? ''} 〜 ${end ?? ''}` : '（未入力）';
  }
  // ⚠️ 増分⑧: file 型はこの関数を経由しない (常に AttachmentFieldInput を disabled で描画する、
  // renderField 側の分岐参照)。ここに来るのは想定外のため素通しでよい。
  return String(value);
};

export function RequestFormFields({
  fields, conditions, formulas, values, onChange, readOnly, maskedFieldIds, nonEditableFieldIds, onResolveWorkDays,
  showAllErrors, onValidationChange, attachmentsByField, attachments,
}: Props) {
  const derived = useMemo(() => computeDerivedValues(fields, formulas, values), [fields, formulas, values]);
  const states = useMemo(() => resolveStates(fields, conditions, derived), [fields, conditions, derived]);

  // 使い勝手フィードバック⑥ (D-U28): blur (項目を離れた) 時点でのみエラーを表示する
  // (入力中の1文字ごとには出さない、§7-5)。提出を試みた後は showAllErrors 経由で全件表示する。
  const [touched, setTouched] = useState<Set<string>>(new Set());
  const markTouched = (id: string) => setTouched((prev) => (prev.has(id) ? prev : new Set(prev).add(id)));
  // 相対日付 (today+Nd) の基準日。フォームを開いている間は日付を跨いでも据え置きでよい
  // (提出はサーバーが提出時点の日付で再検証するため、ここは表示用のスナップショットでよい)。
  const [asOf] = useState(() => new Date().toISOString().slice(0, 10));
  const fieldErrors = useMemo(
    () => validateFormFields(fields, conditions, states, derived, asOf),
    [fields, conditions, states, derived, asOf],
  );

  useEffect(() => { onValidationChange?.(fieldErrors); }, [fieldErrors, onValidationChange]);

  // showAllErrors が false→true に転じた瞬間だけ先頭のエラー項目へフォーカスを移す
  // (§7-5「先頭エラーへフォーカス移動」)。true のまま留まる間に毎レンダーでフォーカスを奪わない。
  const prevShowAllRef = useRef(false);
  useEffect(() => {
    if (showAllErrors && !prevShowAllRef.current && fieldErrors.size > 0) {
      const firstId = [...fieldErrors.keys()][0];
      document.getElementById(`rf-${firstId}`)?.focus();
    }
    prevShowAllRef.current = !!showAllErrors;
  }, [showAllErrors, fieldErrors]);

  const set = (id: string, v: unknown) => onChange({ ...values, [id]: v });

  const masked = new Set(maskedFieldIds ?? []);
  const nonEditable = new Set(nonEditableFieldIds ?? []);
  // 自動入力に使う申請期間。period_end が未紐付けなら開始日のみの1日として扱う (サーバーと同じ規約)。
  const readRoleDate = (role: string): string | null => {
    const f = fields.find((x) => x.semantic_role === role);
    if (!f) return null;
    const v = derived[f.field_definition_id];
    return typeof v === 'string' && v !== '' ? v : null;
  };
  const periodStart = readRoleDate('period_start');
  const periodEnd = readRoleDate('period_end') ?? periodStart;
  const visibleFields = fields.filter((f) => states.get(f.field_definition_id)?.visible !== false);
  if (visibleFields.length === 0) {
    return <p className="text-sm text-text-sub">この申請種別には入力項目が設定されていません。</p>;
  }
  const sections = groupBySection(visibleFields);

  const renderField = (f: FieldDefinition) => {
        const state = states.get(f.field_definition_id)!;
        const value = derived[f.field_definition_id];
        const disabled = readOnly || f.is_computed;
        const labelId = `rf-${f.field_definition_id}`;
        const errorId = `${labelId}-error`;
        // 使い勝手フィードバック⑥ (D-U28): blur 済みか、提出を試みた後 (showAllErrors) だけ表示する。
        const fieldError = (touched.has(f.field_definition_id) || showAllErrors)
          ? fieldErrors.get(f.field_definition_id) : undefined;
        const onFieldBlur = () => markTouched(f.field_definition_id);
        const ariaInvalidProps = fieldError ? { 'aria-invalid': true as const, 'aria-describedby': errorId } : {};
        // 使い勝手フィードバック③ (D-U21): 「値は見えるが編集はできない」項目。
        // ⚠️ readOnly (フォーム全体が読み取り専用) や is_computed (自動計算) とは別の理由のため
        //    見た目・文言を分ける — 3種を同じ `disabled` に潰すと利用者が区別できなくなる (詳細設計 §4-4)。
        const restricted = !readOnly && !f.is_computed && !masked.has(f.field_definition_id)
          && nonEditable.has(f.field_definition_id);
        return (
          <div key={f.field_definition_id}>
            <label htmlFor={labelId} id={`${labelId}-label`} className="block text-sm text-text-body mb-1">
              {f.label}
              {state.required && <span className="text-alert-text ml-1" aria-hidden="true">*</span>}
              {state.required && <span className="sr-only">（必須）</span>}
              {f.is_computed && <span className="ml-2 text-xs text-text-sub">（自動計算）</span>}
              {restricted && <span className="ml-2 text-xs text-text-sub">（編集不可）</span>}
            </label>
            {/* TD-28 D-24: 表示段階 exists_only。項目があることは分かるが値は見せない */}
            {masked.has(f.field_definition_id) ? (
              // ⚠️ label htmlFor の関連付けを切らないため id を持たせる。可視テキストで内容が
              // 伝わるので sr-only の重複読み上げは置かない (5並列レビュー Low)。
              <p id={labelId} className="text-sm text-text-sub bg-surface-sunken rounded-md px-3 py-2 min-h-[44px] sm:min-h-[38px] flex items-center">
                <span aria-hidden="true">●●●</span>
                <span className="ml-2 text-xs">
                  {/* 使い勝手フィードバック増分⑧ (D-U31): level='exists_only' は件数のみ分かる。
                      添付だけ素通しにすると項目は伏せたのに証憑から中身が分かってしまうため、
                      件数だけを添えて内容は伏せる。 */}
                  {f.field_type === 'file' && attachmentsByField?.get(f.field_definition_id)
                    ? `添付ファイル ${attachmentsByField.get(f.field_definition_id)!.count} 件（閲覧権限がないため内容は表示できません）`
                    : '閲覧権限がないため表示できません'}
                </span>
              </p>
            ) : f.field_type === 'file' ? (
              // 使い勝手フィードバック増分⑧ (D-U26/D-U31): editable=false (restricted) の場合も
              // アップロード・削除ボタンを隠すだけで一覧・プレビューは見せる (承認者が中身を
              // 見ずに決裁することを防ぐ、D-U31)。masked 分岐より後に置くことで exists_only を
              // 優先させている。
              <AttachmentFieldInput id={labelId} fieldDefinitionId={f.field_definition_id}
                maxCount={resolveMaxFileCountClient(f)}
                summary={attachmentsByField?.get(f.field_definition_id)}
                disabled={disabled || restricted} callbacks={attachments} />
            ) : restricted && !['textarea', 'number', 'date', 'time', 'text'].includes(f.field_type) ? (
              // テキスト系以外 (真偽値・選択肢・期間・複数日付・ファイル) は native readOnly の意味論が
              // 無いため、値をそのまま表示する読み取り専用ブロックにする (フォーカス可・SR が読み上げる)。
              <p id={labelId} tabIndex={0} aria-readonly="true"
                className="text-sm text-text-body bg-surface-sunken rounded-md px-3 py-2 min-h-[44px] sm:min-h-[38px] flex items-center">
                {formatStaticValue(f, value)}
              </p>
            ) : f.field_type === 'textarea' ? (
              <textarea id={labelId} className={inputCls} rows={3} disabled={disabled} readOnly={restricted}
                aria-readonly={restricted || undefined} {...ariaInvalidProps} onBlur={onFieldBlur}
                value={typeof value === 'string' ? value : ''}
                onChange={(e) => { if (!restricted) set(f.field_definition_id, e.target.value); }} />
            ) : f.field_type === 'number' ? (
              <input id={labelId} type="number" className={inputCls} disabled={disabled} readOnly={restricted}
                aria-readonly={restricted || undefined} {...ariaInvalidProps} onBlur={onFieldBlur}
                value={typeof value === 'number' ? String(value) : ''}
                onChange={(e) => { if (!restricted) set(f.field_definition_id, e.target.value === '' ? null : Number(e.target.value)); }} />
            ) : f.field_type === 'date' ? (
              <input id={labelId} type="date" className={inputCls} disabled={disabled} readOnly={restricted}
                aria-readonly={restricted || undefined} {...ariaInvalidProps} onBlur={onFieldBlur}
                value={typeof value === 'string' ? value : ''}
                onChange={(e) => { if (!restricted) set(f.field_definition_id, e.target.value); }} />
            ) : f.field_type === 'time' ? (
              // TD-28 §18-2: 時間単位年休の時刻 (HH:MM)。サーバーは秒・TZ を持たない形で検証する。
              <input id={labelId} type="time" className={inputCls} disabled={disabled} readOnly={restricted}
                aria-readonly={restricted || undefined} {...ariaInvalidProps} onBlur={onFieldBlur}
                value={typeof value === 'string' ? value : ''}
                onChange={(e) => { if (!restricted) set(f.field_definition_id, e.target.value === '' ? null : e.target.value); }} />
            ) : f.field_type === 'date_range' ? (
              <DateRangeInput id={labelId} value={value} disabled={disabled} invalid={!!fieldError} errorId={errorId}
                onChange={(v) => set(f.field_definition_id, v)} onFieldBlur={onFieldBlur} />
            ) : f.field_type === 'boolean' ? (
              <label className="inline-flex items-center gap-2 min-h-[44px] sm:min-h-[38px] cursor-pointer">
                <input id={labelId} type="checkbox" className="h-5 w-5" disabled={disabled} {...ariaInvalidProps}
                  checked={value === true}
                  onChange={(e) => { set(f.field_definition_id, e.target.checked); markTouched(f.field_definition_id); }} />
                <span className="text-sm text-text-body">はい</span>
              </label>
            ) : f.field_type === 'select' ? (
              <Select value={typeof value === 'string' ? value : ''} onValueChange={(v) => set(f.field_definition_id, v)} disabled={disabled}>
                <SelectTrigger id={labelId} className="w-full" {...ariaInvalidProps} onBlur={onFieldBlur}>
                  <SelectValue placeholder="選択してください" />
                </SelectTrigger>
                <SelectContent>
                  {(f.field_options ?? []).map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            ) : f.field_type === 'multi_date' ? (
              <MultiDateInput
                id={labelId}
                label={f.label}
                value={value}
                disabled={disabled}
                onChange={(v) => { set(f.field_definition_id, v); markTouched(f.field_definition_id); }}
                periodStart={periodStart}
                periodEnd={periodEnd}
                onResolveWorkDays={f.semantic_role === 'consumption_dates' ? onResolveWorkDays : undefined}
                invalid={!!fieldError} errorId={errorId}
              />
            ) : f.field_type === 'multi_select' ? (
              // ⚠️ design-expert HIGH指摘: id={labelId} が無いと先頭エラーへの
              // document.getElementById('rf-'+id)?.focus() が対象を見つけられない。
              // aria-labelledby は実際のラベル文言 (${labelId}-label) を指す (自己参照にしない)。
              <div id={labelId} tabIndex={-1} className="flex flex-wrap gap-2" role="group" aria-labelledby={`${labelId}-label`} {...ariaInvalidProps}>
                {(f.field_options ?? []).map((o) => {
                  const arr = Array.isArray(value) ? (value as string[]) : [];
                  const on = arr.includes(o.value);
                  return (
                    <button key={o.value} type="button" disabled={disabled} aria-pressed={on}
                      className={`min-h-[44px] sm:min-h-[36px] px-3 rounded-md border text-sm ${on ? 'border-misespark-navy bg-misespark-navy text-white' : 'border-surface-border bg-white text-text-body'} disabled:opacity-60`}
                      onClick={() => {
                        set(f.field_definition_id, on ? arr.filter((x) => x !== o.value) : [...arr, o.value]);
                        markTouched(f.field_definition_id);
                      }}>
                      {o.label}
                    </button>
                  );
                })}
              </div>
            ) : (
              <input id={labelId} type="text" className={inputCls} disabled={disabled} readOnly={restricted}
                aria-readonly={restricted || undefined} {...ariaInvalidProps} onBlur={onFieldBlur}
                value={typeof value === 'string' ? value : value === null || value === undefined ? '' : String(value)}
                onChange={(e) => { if (!restricted) set(f.field_definition_id, e.target.value); }} />
            )}
            {/* 使い勝手フィードバック⑥ (D-U28): 項目直下のエラー表示 (§7-5)。 */}
            {fieldError && <p id={errorId} role="alert" className="mt-1 text-xs text-alert-text">{fieldError}</p>}
          </div>
        );
  };

  return (
    <div className="space-y-6">
      {/* 使い勝手フィードバック⑥ (D-U28): 提出失敗時のエラーサマリ (§7-5「出す場所: フォーム先頭」)。
          個々のエラーは項目単位で1件目のみ表示済みのため、ここでは全件をそのまま列挙する。 */}
      {showAllErrors && fieldErrors.size > 0 && (
        <div role="alert" className="rounded-md bg-alert-bg text-alert-text text-sm px-3 py-2">
          <p className="font-medium">入力内容をご確認ください</p>
          <ul className="mt-1 list-disc list-inside space-y-0.5">
            {[...fieldErrors.entries()].map(([id, msg]) => (
              <li key={id}>
                <button type="button" className="underline text-left" onClick={() => document.getElementById(`rf-${id}`)?.focus()}>
                  {msg}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
      {sections.map((section) => (
        <section key={section.key} className="space-y-4">
          {section.label && (
            <h3 className="text-sm font-medium text-text-body border-b border-surface-border pb-1">{section.label}</h3>
          )}
          {section.fields.map(renderField)}
        </section>
      ))}
    </div>
  );
}

function DateRangeInput({ id, value, disabled, onChange, invalid, errorId, onFieldBlur }: {
  id: string; value: unknown; disabled?: boolean; onChange: (v: { start: string; end: string }) => void;
  invalid?: boolean; errorId?: string; onFieldBlur?: () => void;
}) {
  const v = (value && typeof value === 'object' && !Array.isArray(value)) ? value as { start?: string; end?: string } : {};
  const cls = 'min-h-[44px] sm:min-h-[38px] rounded-md border border-surface-border px-3 py-2 text-sm text-text-body bg-white disabled:bg-surface-sunken';
  const ariaInvalidProps = invalid ? { 'aria-invalid': true as const, 'aria-describedby': errorId } : {};
  return (
    <div className="flex flex-wrap items-center gap-2">
      <input id={id} type="date" className={cls} disabled={disabled} value={v.start ?? ''}
        aria-label="開始日" {...ariaInvalidProps} onBlur={onFieldBlur}
        onChange={(e) => onChange({ start: e.target.value, end: v.end ?? '' })} />
      <span className="text-text-sub" aria-hidden="true">〜</span>
      <input type="date" className={cls} disabled={disabled} value={v.end ?? ''}
        aria-label="終了日" {...ariaInvalidProps} onBlur={onFieldBlur}
        onChange={(e) => onChange({ start: v.start ?? '', end: e.target.value })} />
    </div>
  );
}

/**
 * TD-28 §17-1 (D-30): 複数日付 (multi_date) の入力。
 * 日付を1件ずつ足して外す形は TD-26 の MyLeavePage.tsx と同じ (現場が既に慣れている操作)。
 *
 * ⚠️ 「所定労働日を自動入力」は**入力支援**であり、かつ**既存の選択を置き換える破壊的操作**。
 *    既に選択がある場合は必ず確認を挟む (Design レビュー Critical)。
 * ⚠️ 判定できなかった場合 (結果が0件) は**置き換えない**。手で選んだ日付を「判定できませんでした」
 *    という案内だけ残して全消しするのが最悪の挙動 (§17-1(b) の basis='unknown' は
 *    「材料が無い」ことの正しい表現であって、入力を捨ててよいという意味ではない)。
 */
function MultiDateInput({ id, label, value, disabled, onChange, periodStart, periodEnd, onResolveWorkDays, invalid, errorId }: {
  id: string;
  label: string;
  value: unknown;
  disabled?: boolean;
  onChange: (v: string[]) => void;
  periodStart: string | null;
  periodEnd: string | null;
  onResolveWorkDays?: ResolveWorkDaysFn;
  /** 使い勝手フィードバック⑥ (D-U28、design-expert HIGH指摘): エラー状態を SR にも伝える。 */
  invalid?: boolean;
  errorId?: string;
}) {
  const dates = Array.isArray(value) ? (value as unknown[]).filter((v): v is string => typeof v === 'string') : [];
  const [pending, setPending] = useState('');
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [confirmReplace, setConfirmReplace] = useState(false);
  const cls = 'min-h-[44px] sm:min-h-[38px] rounded-md border border-surface-border px-3 py-2 text-sm text-text-body bg-white disabled:bg-surface-sunken disabled:text-text-sub';
  const isDuplicate = pending !== '' && dates.includes(pending);

  const add = () => {
    if (!pending || isDuplicate) return;
    onChange([...dates, pending].sort());
    setNote(`${pending} を追加しました。`);
    setPending('');
  };

  const runAutoFill = async () => {
    if (!onResolveWorkDays || !periodStart || !periodEnd) return;
    setBusy(true);
    setNote(null);
    try {
      const resolved = await onResolveWorkDays(periodStart, periodEnd);
      if (resolved === null) {
        setNote('所定労働日を取得できませんでした。消化する日を手動で選んでください。');
        return;
      }
      const { workDays, fromStandingPattern } = resolved;
      if (workDays.length === 0) {
        // ⚠️ ここで onChange しない。判定できなかったことを理由に既存の選択を消さない。
        setNote('シフトや基本の勤務パターンが登録されていないため、所定労働日を判定できませんでした。'
          + '入力済みの日付はそのままです。消化する日を手動で選んでください。');
        return;
      }
      onChange(workDays);
      // ⚠️ 確定シフトから決まった日と、基本の勤務パターンから推定した日は確からしさが違う。
      //    推定分があることを伝えないと、実際と違う日数がそのまま提出されうる。
      setNote(`${workDays.length}日を所定労働日として入力しました。`
        + (fromStandingPattern > 0
          ? `うち${fromStandingPattern}日はシフトが未確定のため基本の勤務パターンから推定しています。実際と違う場合は追加・削除してください。`
          : '実際と違う場合は追加・削除してください。'));
    } finally {
      setBusy(false);
    }
  };

  const onAutoFillClick = () => {
    if (dates.length > 0) { setConfirmReplace(true); return; }
    void runAutoFill();
  };

  const canAutoFill = !!onResolveWorkDays && !!periodStart && !!periodEnd && !disabled;

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        {/* ⚠️ 期間の外の日付はサーバーが反映時に弾く。入口で範囲を絞り「承認まで通ってから
            失敗する」申請を作らせない (QA MEDIUM-5 の緩和)。 */}
        <input id={id} type="date" className={cls} disabled={disabled} value={pending}
          aria-label={`${label}: 追加する日付`}
          aria-invalid={invalid || undefined} aria-describedby={invalid ? errorId : undefined}
          min={periodStart ?? undefined} max={periodEnd ?? undefined}
          onChange={(e) => setPending(e.target.value)} />
        <button type="button" disabled={disabled || !pending || isDuplicate}
          className="min-h-[44px] sm:min-h-[36px] px-3 rounded-md border border-surface-border bg-white text-sm text-text-body disabled:opacity-60"
          onClick={add}>追加</button>
        {canAutoFill && (
          <button type="button" disabled={busy}
            className="min-h-[44px] sm:min-h-[36px] px-3 rounded-md border border-chrome-base bg-white text-sm text-chrome-base disabled:opacity-60"
            onClick={onAutoFillClick}>
            {busy ? '判定中…' : dates.length > 0 ? '所定労働日で置き換える' : '所定労働日を自動入力'}
          </button>
        )}
        {dates.length > 1 && !disabled && (
          <button type="button"
            className="min-h-[44px] sm:min-h-[36px] px-3 rounded-md border border-surface-border bg-white text-sm text-text-sub"
            onClick={() => { onChange([]); setNote('選択した日付をすべて削除しました。'); }}>すべて削除</button>
        )}
      </div>
      {/* 無言で disabled にせず理由を出す (Nielsen H9) */}
      {isDuplicate && <p className="text-xs text-text-sub">{pending} は既に選択されています。</p>}
      {dates.length > 0 ? (
        <ul className="flex flex-wrap gap-2">
          {dates.map((d) => (
            <li key={d}>
              <span className="inline-flex items-center gap-1 rounded-md border border-surface-border bg-surface-sunken pl-3 text-sm text-text-body">
                {d}
                {!disabled && (
                  <button type="button" aria-label={`${d} を削除`}
                    className="min-h-[44px] sm:min-h-[36px] min-w-[44px] sm:min-w-[36px] text-text-sub"
                    onClick={() => { onChange(dates.filter((x) => x !== d)); setNote(`${d} を削除しました。`); }}>×</button>
                )}
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-text-sub">{disabled ? '日付は登録されていません。' : 'まだ日付が選択されていません。'}</p>
      )}
      {note && <p aria-live="polite" className="text-xs text-text-sub">{note}</p>}
      <ConfirmDialog
        open={confirmReplace}
        title="所定労働日で置き換えますか？"
        message={`選択済みの ${dates.length} 件をすべて破棄し、判定した所定労働日で入れ替えます。`}
        confirmLabel="置き換える"
        destructive
        onConfirm={() => { setConfirmReplace(false); void runAutoFill(); }}
        onClose={() => setConfirmReplace(false)}
      />
    </div>
  );
}

/**
 * 使い勝手フィードバック増分⑧ (D-U26/D-U31): ファイル添付の入力。
 *
 * ⚠️ design-expert H-5: 未保存の新規申請には `request_instance_id` が無く添付できないため、
 *  「先に保存してください」と言わせず、最初のアップロード操作 (ファイル選択) の時点で
 *  `callbacks.ensureRequestInstanceId()` を呼んで下書きを自動作成する。
 * ⚠️ `callbacks` が渡らない画面 (未対応) では読み取り専用の一覧のみ描画する。
 */
function AttachmentFieldInput({ id, fieldDefinitionId, maxCount, summary, disabled, callbacks }: {
  id: string;
  fieldDefinitionId: string;
  maxCount: number;
  summary: AttachmentSummary | undefined;
  disabled?: boolean;
  callbacks?: AttachmentCallbacks;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [previewId, setPreviewId] = useState<string | null>(null);

  const items = summary?.items ?? [];
  const count = summary?.count ?? 0;
  const atLimit = count >= maxCount;
  const canEdit = !disabled && !!callbacks;

  const handleFile = async (file: File) => {
    if (!callbacks) return;
    setError(null);
    if (file.size > MAX_ATTACHMENT_SIZE_BYTES) {
      setError(`ファイルが大きすぎます (上限 ${MAX_ATTACHMENT_SIZE_BYTES / 1024 / 1024}MB、選択されたファイル ${(file.size / 1024 / 1024).toFixed(1)}MB)`);
      if (inputRef.current) inputRef.current.value = '';
      return;
    }
    setBusy(true);
    try {
      const instanceId = callbacks.requestInstanceId ?? await callbacks.ensureRequestInstanceId();
      await callbacks.uploadAttachment(instanceId, fieldDefinitionId, file);
      callbacks.onAttachmentsChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'アップロードに失敗しました');
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const handleDelete = async (attachmentId: string) => {
    if (!callbacks?.requestInstanceId) { setConfirmDeleteId(null); return; }
    setBusy(true);
    setError(null);
    try {
      await callbacks.deleteAttachment(callbacks.requestInstanceId, attachmentId);
      callbacks.onAttachmentsChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : '削除に失敗しました');
    } finally {
      setBusy(false);
      setConfirmDeleteId(null);
    }
  };

  return (
    <div className="space-y-2">
      {canEdit && (
        <>
          <input ref={inputRef} type="file" id={id} className="sr-only"
            accept=".pdf,.jpg,.jpeg,.png,.heic,.xlsx,.docx,.pptx"
            disabled={busy || atLimit}
            onChange={(e) => { const f = e.target.files?.[0]; if (f) void handleFile(f); }} />
          <div className="flex flex-wrap items-center gap-2">
            <button type="button" disabled={busy || atLimit}
              className="min-h-[44px] sm:min-h-[36px] px-3 rounded-md border border-surface-border bg-white text-sm text-text-body disabled:opacity-60"
              onClick={() => inputRef.current?.click()}>
              {busy ? 'アップロード中…' : 'ファイルを選択'}
            </button>
            <span className="text-xs text-text-sub">{count} / {maxCount} 件</span>
          </div>
          {atLimit && <p className="text-xs text-text-sub">添付できる上限に達しています。</p>}
          <p className="text-xs text-text-sub">PDF・画像 (JPEG/PNG/HEIC)・Office ファイル (10MBまで)。</p>
        </>
      )}
      {error && <p role="alert" className="text-xs text-alert-text">{error}</p>}
      {items.length > 0 ? (
        <ul className="space-y-1">
          {items.map((item) => (
            <li key={item.attachment_id} className="rounded-md border border-surface-border bg-white px-3 py-2 space-y-1">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="text-sm text-text-body break-all min-w-0">{item.file_name}</span>
                <div className="flex items-center gap-1 shrink-0">
                  {callbacks?.requestInstanceId && (
                    <button type="button"
                      className="min-h-[44px] sm:min-h-[36px] px-2 rounded-md border border-surface-border bg-white text-xs text-text-body"
                      aria-expanded={previewId === item.attachment_id}
                      aria-controls={`attachment-preview-${item.attachment_id}`}
                      onClick={() => setPreviewId(previewId === item.attachment_id ? null : item.attachment_id)}>
                      {previewId === item.attachment_id ? '閉じる' : 'プレビュー'}
                    </button>
                  )}
                  {canEdit && (
                    <button type="button" aria-label={`${item.file_name} を削除`}
                      className="min-h-[44px] sm:min-h-[36px] min-w-[44px] sm:min-w-[36px] text-text-sub"
                      onClick={() => setConfirmDeleteId(item.attachment_id)}>×</button>
                  )}
                </div>
              </div>
              {previewId === item.attachment_id && callbacks?.requestInstanceId && (
                <div id={`attachment-preview-${item.attachment_id}`}>
                  <AttachmentPreview requestInstanceId={callbacks.requestInstanceId} item={item}
                    fetchAttachmentBlob={callbacks.fetchAttachmentBlob} />
                </div>
              )}
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-text-sub">{canEdit ? 'まだ添付されていません。' : '添付はありません。'}</p>
      )}
      <ConfirmDialog
        open={confirmDeleteId !== null}
        title="添付ファイルを削除しますか？"
        message="削除した添付ファイルは元に戻せません。"
        confirmLabel="削除する" destructive loading={busy}
        onConfirm={() => confirmDeleteId && void handleDelete(confirmDeleteId)}
        onClose={() => setConfirmDeleteId(null)}
      />
    </div>
  );
}

/**
 * 添付1件ぶんのプレビュー。DocumentDetailPage.tsx の Preview と同じ形
 * (fetch (Authorization付き) → blob() → createObjectURL → <img>/<iframe>)。
 * ⚠️ Office 文書・HEIC (弱いマジック検証のため download_only、lib/uploads/mime.ts 参照) は
 *  ブラウザ内で表示できないためダウンロードのみ提供する (D-478 と同じ判断)。
 * ⚠️ PDF は `<iframe sandbox>` では描画できない (§8-153)。sandbox を付けないこと。
 */
function AttachmentPreview({ requestInstanceId, item, fetchAttachmentBlob }: {
  requestInstanceId: string;
  item: AttachmentItem;
  fetchAttachmentBlob: (requestInstanceId: string, attachmentId: string) => Promise<Blob>;
}) {
  const [src, setSrc] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const inlineViewable = item.mime_type === 'application/pdf'
    || (item.mime_type.startsWith('image/') && item.mime_type !== 'image/heic');

  useEffect(() => {
    if (!inlineViewable) return undefined;
    let revoked = '';
    let cancelled = false;
    void (async () => {
      try {
        const blob = await fetchAttachmentBlob(requestInstanceId, item.attachment_id);
        if (cancelled) return;
        const url = URL.createObjectURL(blob);
        revoked = url;
        setSrc(url);
      } catch {
        if (!cancelled) setFailed(true);
      }
    })();
    // ⚠️ revokeObjectURL を忘れるとブラウザにファイルが残り続ける (メモリリーク)
    return () => { cancelled = true; if (revoked) URL.revokeObjectURL(revoked); };
  }, [requestInstanceId, item.attachment_id, inlineViewable]);

  const download = async () => {
    try {
      const blob = await fetchAttachmentBlob(requestInstanceId, item.attachment_id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = item.file_name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      // ⚠️ QAレビュー指摘: click() 直後に同期で revoke すると一部ブラウザ (Safari系) で
      // ダウンロードが開始する前に URL が無効化され失敗することがある。遅延させて revoke する。
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch {
      setFailed(true);
    }
  };

  if (!inlineViewable) {
    return (
      <div className="text-xs text-text-sub space-y-1">
        <p>この形式はブラウザ内で表示できないため、開くと端末に保存されます。</p>
        <button type="button"
          className="min-h-[44px] sm:min-h-[36px] px-2 rounded-md border border-surface-border bg-white text-text-body"
          onClick={() => void download()}>ダウンロード</button>
      </div>
    );
  }
  if (failed) return <p className="text-xs text-alert-text">プレビューを表示できませんでした。</p>;
  if (!src) return <p className="text-xs text-text-sub">読み込み中…</p>;
  if (item.mime_type.startsWith('image/')) {
    return (
      <img src={src} alt={`${item.file_name} のプレビュー`}
        className="max-w-full h-auto border border-surface-border rounded bg-white" />
    );
  }
  return (
    <iframe src={src} title={`${item.file_name} のプレビュー`}
      className="w-full h-[50vh] min-h-[240px] border border-surface-border rounded bg-white" />
  );
}
